/**
 * useJobDetail Hook
 *
 * React Query hook for fetching a single job's details.
 */

import { queryKeys } from '@/lib/query-client';
import type { AddJobTaskBody } from '@skewb-works/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addJobTask, getJobDetail, removeJobTask } from '../api/jobDetailApi';

export function useJobDetail(jobId: number) {
  const query = useQuery({
    queryKey: queryKeys.jobs.detail(String(jobId)),
    queryFn: () => getJobDetail(jobId),
    enabled: jobId > 0,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useAddJobTask(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AddJobTaskBody) => addJobTask(jobId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(String(jobId)) });
    },
    meta: {
      successTitle: 'Task Added',
      successMessage: 'Job task added successfully',
    },
  });
}

export function useRemoveJobTask(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: number) => removeJobTask(jobId, taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(String(jobId)) });
    },
    meta: {
      successTitle: 'Task Removed',
      successMessage: 'Job task removed successfully',
    },
  });
}
